import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import heroSettingsLight from "../assets/images/hero-settings-light-pexels.jpeg";
import heroSettingsDark from "../assets/images/hero-settings-dark-pexels.jpeg";
import { apiClient } from "../lib/apiClient";

interface Institution {
  id: string;
  name: string;
  website?: string | null;
}

interface Course {
  id: string;
  code: string;
  title: string;
  institutionId: string | null;
  institutionName: string | null;
  role: string;
  startDate: string | null;
  endDate: string | null;
}

interface KeyDate {
  id: string;
  courseId: string;
  title: string;
  dueDate: string;
}

interface ApiLoad {
  institutions: Institution[];
  courses: Course[];
  keyDates: KeyDate[];
}

export default function SettingsPage() {
  const tabs = ["Institutions", "Courses", "Key Dates"] as const;
  type Tab = (typeof tabs)[number];
  const [activeTab, setActiveTab] = useState<Tab>("Institutions");
  const [loading, setLoading] = useState(true);

  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [keyDates, setKeyDates] = useState<KeyDate[]>([]);

  const [showInstitutionModal, setShowInstitutionModal] = useState(false);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [showKeyDateModal, setShowKeyDateModal] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<string>("");

  useEffect(() => {
    apiClient.functions
      .invoke<ApiLoad>("educator_settings", { body: { action: "load" } })
      .then(({ data, error }) => {
        if (!error && data) {
          setInstitutions(data.institutions ?? []);
          setCourses(data.courses ?? []);
          setKeyDates(data.keyDates ?? []);
          if ((data.courses ?? []).length > 0) {
            setSelectedCourseId(data.courses[0].id);
          }
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const renderInstitutions = () => (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h2 className="text-lg font-medium">Institutions</h2>
        <button
          onClick={() => setShowInstitutionModal(true)}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white shadow hover:bg-primary/90"
        >
          <Plus size={16} /> Add Institution
        </button>
      </header>

      <div className="overflow-x-auto rounded-lg border bg-white dark:bg-gray-800 dark:border-gray-700 shadow-sm">
        <table className="min-w-full divide-y">
          <thead className="bg-gray-50 dark:bg-gray-700 text-left text-sm font-medium dark:text-gray-300">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Website</th>
            </tr>
          </thead>
          <tbody className="divide-y text-sm">
            {institutions.map((inst) => (
              <tr key={inst.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-medium">{inst.name}</td>
                <td className="px-4 py-2 text-primary underline">
                  {inst.website ? (
                    <a href={inst.website} target="_blank" rel="noreferrer">
                      {inst.website.replace(/^https?:\/\//, "")}
                    </a>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
            {institutions.length === 0 && !loading && (
              <tr>
                <td colSpan={2} className="px-4 py-6 text-center text-gray-500">
                  No institutions added yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showInstitutionModal && (
        <InstitutionModal
          onClose={() => setShowInstitutionModal(false)}
          onSave={async ({ name, website }) => {
            const { data, error } = await apiClient.functions.invoke<{ institution: Institution }>(
              "educator_settings",
              { body: { action: "add_institution", name, website } },
            );
            if (!error && data?.institution) {
              setInstitutions((prev) => [...prev, data.institution]);
              setShowInstitutionModal(false);
            }
          }}
        />
      )}
    </div>
  );

  const renderCourses = () => (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h2 className="text-lg font-medium">Courses</h2>
        <button
          onClick={() => setShowCourseModal(true)}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white shadow hover:bg-primary/90"
        >
          <Plus size={16} /> Add Course
        </button>
      </header>

      <div className="overflow-x-auto rounded-lg border bg-white dark:bg-gray-800 dark:border-gray-700 shadow-sm">
        <table className="min-w-full divide-y">
          <thead className="bg-gray-50 dark:bg-gray-700 text-left text-sm font-medium dark:text-gray-300">
            <tr>
              <th className="px-4 py-2">Code</th>
              <th className="px-4 py-2">Title</th>
              <th className="px-4 py-2">Institution</th>
              <th className="px-4 py-2">Role</th>
              <th className="px-4 py-2">Start</th>
              <th className="px-4 py-2">End</th>
            </tr>
          </thead>
          <tbody className="divide-y text-sm">
            {courses.map((course) => (
              <tr key={course.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-medium">{course.code}</td>
                <td className="px-4 py-2">{course.title}</td>
                <td className="px-4 py-2">{course.institutionName ?? "—"}</td>
                <td className="px-4 py-2">{course.role}</td>
                <td className="px-4 py-2">{course.startDate ?? "—"}</td>
                <td className="px-4 py-2">{course.endDate ?? "—"}</td>
              </tr>
            ))}
            {courses.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                  No courses added yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showCourseModal && (
        <CourseModal
          onClose={() => setShowCourseModal(false)}
          institutions={institutions}
          onSave={async ({ code, title, institutionId, role, startDate, endDate }) => {
            const { data, error } = await apiClient.functions.invoke<{ course: Course }>(
              "educator_settings",
              { body: { action: "add_course", code, title, institutionId, role, startDate, endDate } },
            );
            if (!error && data?.course) {
              setCourses((prev) => [...prev, data.course]);
              if (!selectedCourseId) setSelectedCourseId(data.course.id);
              setShowCourseModal(false);
            }
          }}
        />
      )}
    </div>
  );

  const renderKeyDates = () => {
    const filteredDates = keyDates.filter((d) => d.courseId === selectedCourseId);
    return (
      <div className="space-y-4">
        <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-medium">Key Dates</h2>
            <select
              className="rounded-md border px-2 py-1 text-sm"
              value={selectedCourseId}
              onChange={(e) => setSelectedCourseId(e.target.value)}
            >
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} – {c.title}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={() => setShowKeyDateModal(true)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white shadow hover:bg-primary/90"
          >
            <Plus size={16} /> Add Key Date
          </button>
        </header>

        <ul className="space-y-3">
          {filteredDates.map((d) => (
            <li
              key={d.id}
              className="flex items-center justify-between rounded-lg border bg-white px-4 py-3 shadow-sm"
            >
              <span className="font-medium">{d.title}</span>
              <span className="text-sm text-gray-600">{d.dueDate}</span>
            </li>
          ))}
          {filteredDates.length === 0 && (
            <li className="rounded-lg border bg-white px-4 py-6 text-center text-gray-500 shadow-sm">
              No key dates for this course.
            </li>
          )}
        </ul>

        {showKeyDateModal && (
          <KeyDateModal
            onClose={() => setShowKeyDateModal(false)}
            courseId={selectedCourseId}
            onSave={async ({ title, dueDate }) => {
              const { data, error } = await apiClient.functions.invoke<{ keyDate: KeyDate }>(
                "educator_settings",
                { body: { action: "add_key_date", courseId: selectedCourseId, title, dueDate } },
              );
              if (!error && data?.keyDate) {
                setKeyDates((prev) => [...prev, data.keyDate]);
                setShowKeyDateModal(false);
              }
            }}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <section className="relative mb-8 overflow-hidden rounded-2xl">
        {/* Theme-specific hero */}
        <img
          src={heroSettingsLight}
          alt="Teacher adjusting settings in bright workspace"
          className="theme-light-only absolute inset-0 h-56 w-full object-cover md:h-72"
        />
        <img
          src={heroSettingsDark}
          alt="Educator working on laptop in dim office"
          className="theme-dark-only absolute inset-0 h-56 w-full object-cover md:h-72"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/40 to-white" />
        <div className="relative z-10 p-6">
          <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
          <p className="mt-1 text-gray-700">Manage your institutions, courses, and key dates.</p>
          {/* Credits – light */}
          <p className="mt-2 text-xs text-gray-500 dark:hidden">
            Photo by{' '}
            <a
              href="https://www.pexels.com/@ketut-subiyanto"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Ketut Subiyanto
            </a>{' '}
            on{' '}
            <a
              href="https://www.pexels.com"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Pexels
            </a>
          </p>

          {/* Credits – dark */}
          <p className="mt-2 hidden text-xs text-gray-400 dark:block">
            Photo by{' '}
            <a
              href="https://www.pexels.com/@czapparpad"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Czapp Árpád
            </a>{' '}
            on{' '}
            <a
              href="https://www.pexels.com"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Pexels
            </a>
          </p>
        </div>
      </section>

      <div className="space-y-6">

      {/* Tabs */}
      <div className="overflow-x-auto">
        <nav className="flex gap-2 border-b">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-600 hover:text-primary"
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {loading ? (
        <p className="text-center text-sm text-gray-500">Loading…</p>
      ) : (
        <>
          {activeTab === "Institutions" && renderInstitutions()}
          {activeTab === "Courses" && renderCourses()}
          {activeTab === "Key Dates" && renderKeyDates()}
        </>
      )}
      </div>
    </>
  );
}

/* =========================================================
 * Modals (very lightweight, local state only)
 * =======================================================*/

interface ModalProps {
  children: React.ReactNode;
  onClose: () => void;
}

function ModalBase({ children, onClose }: ModalProps) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-label="Close backdrop"
      />
      <div className="relative z-50 w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
        <button
          onClick={onClose}
          className="absolute right-3 top-3 rounded p-1 text-gray-500 hover:bg-gray-100"
          aria-label="Close modal"
        >
          <X size={18} />
        </button>
        {children}
      </div>
    </div>
  );
}

/* ----------------- Institution Modal -----------------*/
interface InstitutionModalProps {
  onClose: () => void;
  onSave: (data: { name: string; website: string }) => void;
}
function InstitutionModal({ onClose, onSave }: InstitutionModalProps) {
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  return (
    <ModalBase onClose={onClose}>
      <h3 className="mb-4 text-lg font-medium">Add Institution</h3>
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium">Name *</label>
          <input
            type="text"
            className="w-full rounded-md border px-3 py-2 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="University name"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Website</label>
          <input
            type="url"
            className="w-full rounded-md border px-3 py-2 text-sm"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://example.edu"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={() => name.trim() && onSave({ name: name.trim(), website: website.trim() })}
            disabled={!name.trim()}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white shadow hover:bg-primary/90 disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </ModalBase>
  );
}

/* ----------------- Course Modal -----------------*/
interface CourseFormData {
  code: string;
  title: string;
  institutionId: string;
  role: string;
  startDate: string;
  endDate: string;
}
interface CourseModalProps {
  onClose: () => void;
  onSave: (data: CourseFormData) => void;
  institutions: { id: string; name: string }[];
}
function CourseModal({ onClose, onSave, institutions }: CourseModalProps) {
  const [code, setCode] = useState("");
  const [title, setTitle] = useState("");
  const [institutionId, setInstitutionId] = useState<string>(institutions[0]?.id ?? "");
  const [role, setRole] = useState("Instructor");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  return (
    <ModalBase onClose={onClose}>
      <h3 className="mb-4 text-lg font-medium">Add Course</h3>
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium">Institution *</label>
          <select
            className="w-full rounded-md border px-3 py-2 text-sm"
            value={institutionId}
            onChange={(e) => setInstitutionId(e.target.value)}
          >
            {institutions.map((inst) => (
              <option key={inst.id} value={inst.id}>
                {inst.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="mb-1 block text-sm font-medium">Course Code *</label>
            <input
              type="text"
              className="w-full rounded-md border px-3 py-2 text-sm"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="CS101"
            />
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-sm font-medium">Role *</label>
            <select
              className="w-full rounded-md border px-3 py-2 text-sm"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              {[
                "Instructor",
                "Teaching Assistant",
                "Marker",
                "Observer",
              ].map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Course Title *</label>
          <input
            type="text"
            className="w-full rounded-md border px-3 py-2 text-sm"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Intro to Computer Science"
          />
        </div>
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="mb-1 block text-sm font-medium">Start Date *</label>
            <input
              type="date"
              className="w-full rounded-md border px-3 py-2 text-sm"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-sm font-medium">End Date *</label>
            <input
              type="date"
              className="w-full rounded-md border px-3 py-2 text-sm"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={() =>
              code && title && startDate && endDate &&
              onSave({ code, title, institutionId, role, startDate, endDate })
            }
            disabled={!(code && title && startDate && endDate)}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white shadow hover:bg-primary/90 disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </ModalBase>
  );
}

/* ----------------- Key Date Modal -----------------*/
interface KeyDateModalProps {
  onClose: () => void;
  onSave: (data: { title: string; dueDate: string }) => void;
  courseId: string;
}
function KeyDateModal({ onClose, onSave }: KeyDateModalProps) {
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  return (
    <ModalBase onClose={onClose}>
      <h3 className="mb-4 text-lg font-medium">Add Key Date</h3>
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium">Title *</label>
          <input
            type="text"
            className="w-full rounded-md border px-3 py-2 text-sm"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Assignment 1 due"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Date *</label>
          <input
            type="date"
            className="w-full rounded-md border px-3 py-2 text-sm"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={() => title && dueDate && onSave({ title, dueDate })}
            disabled={!(title && dueDate)}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white shadow hover:bg-primary/90 disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </ModalBase>
  );
}
